import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateInboxMutation, useUpdateInboxMutation } from '@/store/emailInboxApi';
import { EmailInboxConfig, EmailInboxConfigPayload, InboxProtocol, InboxLabel, InboxDirection } from '@/types/EmailInbox';
import { toast } from 'sonner';
import { X, Plus } from 'lucide-react';
import { GmailConnectSection } from './GmailConnectSection';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initialData: EmailInboxConfig | null;
  /** Pre-select a protocol for new-inbox flow (e.g. when "Add Gmail" is clicked). */
  initialProtocol?: InboxProtocol;
}

const EMPTY_IMAP_POP3: EmailInboxConfigPayload = {
  label: 'support',
  protocol: 'imap',
  direction: 'send_receive',
  email: '',
  imap_host: '',
  imap_port: 993,
  imap_user: '',
  imap_password_input: '',
  imap_folders: [],
  pop3_host: '',
  pop3_port: 995,
  pop3_user: '',
  pop3_password_input: '',
  default_department: null,
  is_active: true,
};

export function InboxFormModal({ open, onOpenChange, initialData, initialProtocol = 'imap' }: Props) {
  const isEdit = !!initialData;
  const [createInbox, { isLoading: isCreating }] = useCreateInboxMutation();
  const [updateInbox, { isLoading: isUpdating }] = useUpdateInboxMutation();
  const isSubmitting = isCreating || isUpdating;

  const [form, setForm] = useState<EmailInboxConfigPayload>(EMPTY_IMAP_POP3);
  const [folderInput, setFolderInput] = useState('');

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          label: initialData.label,
          protocol: initialData.protocol,
          direction: initialData.direction,
          email: initialData.email,
          imap_host: initialData.imap_host,
          imap_port: initialData.imap_port,
          imap_user: initialData.imap_user,
          imap_password_input: '',
          imap_folders: initialData.imap_folders ?? [],
          pop3_host: initialData.pop3_host,
          pop3_port: initialData.pop3_port,
          pop3_user: initialData.pop3_user,
          pop3_password_input: '',
          default_department: initialData.default_department,
          is_active: initialData.is_active,
        });
      } else {
        setForm({ ...EMPTY_IMAP_POP3, protocol: initialProtocol ?? 'imap' });
      }
      setFolderInput('');
    }
  }, [open, initialData, initialProtocol]);

  function set<K extends keyof EmailInboxConfigPayload>(key: K, value: EmailInboxConfigPayload[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addFolder(value: string) {
    const trimmed = value.trim();
    if (!trimmed) return;
    const current = form.imap_folders ?? [];
    if (!current.includes(trimmed)) {
      set('imap_folders', [...current, trimmed]);
    }
    setFolderInput('');
  }

  function removeFolder(value: string) {
    set('imap_folders', (form.imap_folders ?? []).filter((f) => f !== value));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      const payload: EmailInboxConfigPayload = { ...form };
      if (isEdit) {
        await updateInbox({ id: initialData!.id, ...payload }).unwrap();
        toast.success('התיבה עודכנה בהצלחה');
      } else {
        // For IMAP/POP3 we need the email field — it's part of the form via 'email' input below
        await createInbox({ ...payload, email: (payload as Record<string, unknown>)['email'] as string }).unwrap();
        toast.success('התיבה נוצרה בהצלחה');
      }
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = (err as { data?: { detail?: string } })?.data?.detail ?? 'שגיאה בשמירה';
      toast.error(msg);
    }
  }

  const protocol = form.protocol as InboxProtocol;
  const isGmail = protocol === 'google_oauth';

  // Gmail new-inbox: the form is handled by GmailConnectSection (OAuth redirect flow)
  // so we just show that section and no submit button.

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? `עריכת תיבה — ${initialData?.email}`
              : 'הוספת תיבה חדשה'}
          </DialogTitle>
        </DialogHeader>

        {/* ─── Gmail protocol ─────────────────────────────────────── */}
        {isGmail ? (
          <div className="space-y-4 mt-2">
            {/* Protocol selector (to allow switching away from Gmail) */}
            <div className="space-y-1">
              <Label>פרוטוקול</Label>
              <ProtocolSelect value={protocol} onChange={(v) => set('protocol', v)} />
            </div>

            <GmailConnectSection
              initialData={initialData}
              selectedLabel={form.label}
              onLabelChange={(v) => set('label', v)}
            />

            {/* For existing Gmail inboxes: allow editing label + active toggle */}
            {isEdit && (
              <form onSubmit={handleSubmit} className="space-y-4 border-t pt-4">
                <LabelSelect value={form.label} onChange={(v) => set('label', v)} />
                <DirectionSelect value={form.direction || 'send_receive'} onChange={(v) => set('direction', v)} />
                <ActiveToggle checked={form.is_active ?? true} onChange={(v) => set('is_active', v)} />
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'שומר...' : 'שמור שינויים'}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </div>
        ) : (
          /* ─── IMAP / POP3 protocol ──────────────────────────────── */
          <form onSubmit={handleSubmit} className="space-y-5 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {!isEdit && (
                <div className="space-y-1 sm:col-span-2">
                  <Label>כתובת מייל *</Label>
                  <Input
                    type="email"
                    value={(form as Record<string, unknown>)['email'] as string ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value } as EmailInboxConfigPayload))}
                    required
                    placeholder="inbox@example.com"
                  />
                </div>
              )}
              <LabelSelect value={form.label} onChange={(v) => set('label', v)} />
              <DirectionSelect value={form.direction || 'send_receive'} onChange={(v) => set('direction', v)} />
              <div className="space-y-1">
                <Label>פרוטוקול</Label>
                <ProtocolSelect value={protocol} onChange={(v) => set('protocol', v)} />
              </div>
            </div>

            {/* IMAP */}
            {protocol === 'imap' && (
              <div className="space-y-3 border rounded-md p-4">
                <p className="text-sm font-medium text-gray-700">הגדרות IMAP</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FieldInput label="שרת IMAP" value={form.imap_host ?? ''} onChange={(v) => set('imap_host', v)} placeholder="imap.example.com" />
                  <FieldInput label="פורט" type="number" value={String(form.imap_port ?? 993)} onChange={(v) => set('imap_port', Number(v))} />
                  <FieldInput label="שם משתמש" value={form.imap_user ?? ''} onChange={(v) => set('imap_user', v)} />
                  <FieldInput
                    label={isEdit ? 'סיסמה (ריק = ללא שינוי)' : 'סיסמה *'}
                    type="password"
                    value={form.imap_password_input ?? ''}
                    onChange={(v) => set('imap_password_input', v)}
                    required={!isEdit}
                  />
                </div>
                <FolderManager
                  label="תיקיות לניטור"
                  placeholder="INBOX"
                  folders={form.imap_folders ?? []}
                  inputValue={folderInput}
                  onInputChange={setFolderInput}
                  onAdd={addFolder}
                  onRemove={removeFolder}
                />
              </div>
            )}

            {/* POP3 */}
            {protocol === 'pop3' && (
              <div className="space-y-3 border rounded-md p-4">
                <p className="text-sm font-medium text-gray-700">הגדרות POP3</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <FieldInput label="שרת POP3" value={form.pop3_host ?? ''} onChange={(v) => set('pop3_host', v)} placeholder="pop.example.com" />
                  <FieldInput label="פורט" type="number" value={String(form.pop3_port ?? 995)} onChange={(v) => set('pop3_port', Number(v))} />
                  <FieldInput label="שם משתמש" value={form.pop3_user ?? ''} onChange={(v) => set('pop3_user', v)} />
                  <FieldInput
                    label={isEdit ? 'סיסמה (ריק = ללא שינוי)' : 'סיסמה *'}
                    type="password"
                    value={form.pop3_password_input ?? ''}
                    onChange={(v) => set('pop3_password_input', v)}
                    required={!isEdit}
                  />
                </div>
              </div>
            )}

            <ActiveToggle checked={form.is_active ?? true} onChange={(v) => set('is_active', v)} />

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>ביטול</Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'שומר...' : isEdit ? 'שמור שינויים' : 'צור תיבה'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Small reusable sub-components ─────────────────────────────────────── */

function ProtocolSelect({ value, onChange }: { value: string; onChange: (v: InboxProtocol) => void }) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as InboxProtocol)}>
      <SelectTrigger>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="imap">IMAP</SelectItem>
        <SelectItem value="pop3">POP3</SelectItem>
        <SelectItem value="google_oauth">Gmail (Google OAuth)</SelectItem>
      </SelectContent>
    </Select>
  );
}

function LabelSelect({ value, onChange }: { value: string; onChange: (v: InboxLabel) => void }) {
  return (
    <div className="space-y-1">
      <Label>תיאור</Label>
      <Select value={value} onValueChange={(v) => onChange(v as InboxLabel)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="support">שירות לקוחות</SelectItem>
          <SelectItem value="sales">מכירות</SelectItem>
          <SelectItem value="billing">גביה</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function DirectionSelect({ value, onChange }: { value: InboxDirection; onChange: (v: InboxDirection) => void }) {
  return (
    <div className="space-y-1">
      <Label>מצב תיבה</Label>
      <Select value={value} onValueChange={(v) => onChange(v as InboxDirection)}>
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="send_receive">שליחה + קבלה</SelectItem>
          <SelectItem value="send_only">שליחה בלבד</SelectItem>
          <SelectItem value="receive_only">קבלה בלבד</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function ActiveToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-3">
      <Switch id="is_active_toggle" checked={checked} onCheckedChange={onChange} />
      <Label htmlFor="is_active_toggle">תיבה פעילה</Label>
    </div>
  );
}

function FieldInput({
  label, value, onChange, type = 'text', placeholder, required,
}: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} />
    </div>
  );
}

function FolderManager({
  label, placeholder, folders, inputValue, onInputChange, onAdd, onRemove,
}: {
  label: string; placeholder: string; folders: string[];
  inputValue: string; onInputChange: (v: string) => void;
  onAdd: (v: string) => void; onRemove: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAdd(inputValue); } }}
        />
        <Button type="button" variant="outline" size="sm" onClick={() => onAdd(inputValue)}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {folders.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {folders.map((f) => (
            <span key={f} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded">
              {f}
              <button type="button" onClick={() => onRemove(f)} className="text-gray-400 hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

